import type { MutableRefObject } from 'react';
import { useCallback, useMemo } from 'react';
import { useContextSelector } from 'use-context-selector';
import type {
  Active,
  DragCancelEvent,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragStartEvent,
  Over,
  UseDraggableArguments,
  UseDroppableArguments,
} from '@dnd-kit/core';
import {
  MeasuringStrategy,
  useDndMonitor,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import type {
  SortableContextProps,
  UseSortableArguments,
} from '@dnd-kit/sortable';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { theme } from '@frameio/vapor';
import { defaultDndMeasuring, GlobalDndContext } from '../contexts/DndContext';
import type { GlobalDndNamespace } from '.';
import type { GlobalDndData } from './GlobalDndData';

const DEFAULT_TRANSITION = {
  easing: theme.transitions.eases.easeOut,
  duration: 250,
};

type UseGlobalDroppableReturn = Globalize<ReturnType<typeof useDroppable>>;
type UseGlobalDroppableArguments = StringID<GlobalData<UseDroppableArguments>>;

/** extends dnd-kit's useDroppable to be typed and globally namespaced */
export const useGlobalDroppable = (args: UseGlobalDroppableArguments) =>
  applyId(
    useDroppable({
      ...args,
      id: getNamespacedId(args.id, args.data.namespace),
    })
  ) as UseGlobalDroppableReturn;

type UseGlobalDraggableArguments = StringID<GlobalData<UseDraggableArguments>>;
type UseGlobalDraggableReturn = Globalize<ReturnType<typeof useDraggable>>;

/** extends dnd-kit's useDraggable to be typed and globally namespaced */
export const useGlobalDraggable = (args: UseGlobalDraggableArguments) =>
  applyId(
    useDraggable({
      ...args,
      id: getNamespacedId(args.id, args.data.namespace),
    })
  ) as UseGlobalDraggableReturn;

type UseGlobalSortableArguments = Partial<
  StringID<GlobalData<UseSortableArguments>>
>;
type UseGlobalSortableReturn = Globalize<ReturnType<typeof useSortable>>;

/** extends dnd-kit's useSortable to be typed and globally namespaced */
export const useGlobalSortable = (args: UseGlobalSortableArguments) =>
  applyId(
    useSortable({
      ...args,
      transition: DEFAULT_TRANSITION,
      id: getNamespacedId(args.id, args?.data?.namespace),
    })
  ) as UseGlobalSortableReturn;

export type UseGlobalDndMonitorArgs = {
  onDragStart?: (event: Globalize<DragStartEvent>) => void;
  onDragMove?: (event: Globalize<DragMoveEvent>) => void;
  onDragOver?: (event: Globalize<DragOverEvent>) => void;
  onDragEnd?: (event: Globalize<DragEndEvent>) => void;
  onDragCancel?: (event: Globalize<DragCancelEvent>) => void;
};

/** extends dnd-kit's useDndMonitor to be typed and globally namespaced */
export const useGlobalDndMonitor = ({
  onDragCancel: onDragCancelHandler,
  onDragEnd: onDragEndHandler,
  onDragMove: onDragMoveHandler,
  onDragOver: onDragOverHandler,
  onDragStart: onDragStartHandler,
}: UseGlobalDndMonitorArgs) =>
  useDndMonitor({
    onDragCancel: useCallback(
      (evt: DragCancelEvent) => {
        onDragCancelHandler?.(applyId(evt) as Globalize<DragCancelEvent>);
      },
      [onDragCancelHandler]
    ),
    onDragEnd: useCallback(
      (evt: DragEndEvent) => {
        onDragEndHandler?.(applyId(evt) as Globalize<DragEndEvent>);
      },
      [onDragEndHandler]
    ),
    onDragMove: useCallback(
      (evt: DragMoveEvent) => {
        onDragMoveHandler?.(applyId(evt) as Globalize<DragMoveEvent>);
      },
      [onDragMoveHandler]
    ),
    onDragOver: useCallback(
      (evt: DragOverEvent) => {
        onDragOverHandler?.(applyId(evt) as Globalize<DragOverEvent>);
      },
      [onDragOverHandler]
    ),
    onDragStart: useCallback(
      (evt: DragStartEvent) => {
        onDragStartHandler?.({
          ...evt,
          active: { ...evt.active, id: getId(evt.active.id as string).id },
        } as Globalize<DragStartEvent>);
      },
      [onDragStartHandler]
    ),
  });

/** extends dnd-kit's SortableContext to be typed and globally namespaced */
export const GlobalSortableContext = ({
  children,
  items,
  namespace,
  ...props
}: Omit<SortableContextProps, 'items'> & {
  items: string[];
  namespace?: GlobalDndNamespace;
}) => {
  const namespacedItems = useMemo(
    () => items.map((item) => getNamespacedId(item, namespace)),
    [items, namespace]
  );
  return (
    <SortableContext items={namespacedItems} {...props}>
      {children}
    </SortableContext>
  );
};

/**
 * set dnd-kit prop overrides on the global dnd-kit context
 *
 * @warn
 * this will override props for ALL global dnd use cases!
 * use with caution, and make sure to reset to `null` when your dragging use case is complete
 */
export const useSetGlobalDndContextProps = () =>
  useContextSelector(GlobalDndContext, (val) => val.setContextProps);

/**
 * forces dnd-kit to remeasure all droppables. this is useful in cases where
 * droppables are animating or changing size _while_ dragging is happening
 *
 * we force remeasure by temporarily setting the dnd context measuring frequency
 * from the default `MeasuringFrequency.Optimized` to 50ms. after 100ms we reset
 * the props back to default
 */
export const useForceGlobalDndRemeasure = () => {
  const setContextProps = useSetGlobalDndContextProps();

  return useCallback(() => {
    setContextProps({
      measuring: {
        ...defaultDndMeasuring,
        droppable: {
          ...defaultDndMeasuring.droppable,
          strategy: MeasuringStrategy.Always,
          frequency: 50,
        },
      },
    });
    setTimeout(() => {
      setContextProps(null);
    }, 100);
  }, [setContextProps]);
};
/**
 * setter to render an overlay while dragging a global dnd item
 * call with `null` when the drag is done or canceled to remove the overlay
 */
export const useSetGlobalDndOverlay = () =>
  useContextSelector(GlobalDndContext, (val) => val.setOverlay);

/** utils */
const getNamespacedId = <TNamespace extends string = string>(
  id: string,
  namespace?: TNamespace
) => (namespace ? `${namespace}:${id}` : id);

const getId = <TNamespace extends string = string>(dndId?: string) => {
  if (!dndId) return { id: null, namespace: null };
  const split = dndId.split(`:`);
  return {
    namespace: split[0] as TNamespace,
    id: split[1],
  };
};

/** applies namespaced id to active and over fields in a dnd event  */
const applyId = <T extends { active: Active; over: Over }>(evt: T) => ({
  ...evt,
  active: {
    ...evt.active,
    id: getId(evt.active?.id as string).id,
  },
  over: {
    ...evt.over,
    id: getId(evt.over?.id as string).id,
  },
});

type DataRef = MutableRefObject<GlobalDndData>;

/** overrides the dndkit types to reflect a `data` type of `GlobalDndData` */
type Globalize<T extends { active?: Active; over?: Over }> = {
  [K in keyof T]: T[K] extends Active
    ? Omit<Active, 'data'> & { data: DataRef }
    : T[K] extends Over
    ? Omit<Over, 'data'> & { data: DataRef }
    : T[K];
};

type GlobalData<T extends { data?: Record<string, unknown> }> = Omit<
  T,
  'data'
> & {
  data: GlobalDndData;
};

type StringID<T extends { id: unknown }> = Omit<T, 'id'> & { id: string };
